import cornerstone from 'cornerstone-core';

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const normalize = a => {
  const L = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / L, a[1] / L, a[2] / L];
};

/** Model the CT volume as a regular grid. */
export function buildCtVolumeGeometry(ctImageIds) {
  const planes = ctImageIds
    .map(id => {
      const p = cornerstone.metaData.get('imagePlaneModule', id);
      if (!p || !p.imagePositionPatient || !p.rowCosines || !p.columnCosines) {
        return null;
      }
      return {
        id,
        ipp: p.imagePositionPatient,
        rowCos: normalize(p.rowCosines),
        colCos: normalize(p.columnCosines),
        colSp: p.columnPixelSpacing,
        rowSp: p.rowPixelSpacing,
        rows: p.rows,
        columns: p.columns,
      };
    })
    .filter(Boolean);
  if (planes.length < 2) return null;

  const rowCos = planes[0].rowCos;
  const colCos = planes[0].colCos;
  const normal = normalize(cross(rowCos, colCos));

  const withProj = planes
    .map(p => ({ ...p, proj: dot(p.ipp, normal) }))
    .sort((a, b) => a.proj - b.proj);

  const proj0 = withProj[0].proj;
  const projLast = withProj[withProj.length - 1].proj;
  const dz = (projLast - proj0) / (withProj.length - 1);
  if (!(dz > 0)) return null;

  return {
    ipp0: withProj[0].ipp,
    rowCos,
    colCos,
    normal,
    colSp: withProj[0].colSp,
    rowSp: withProj[0].rowSp,
    dz,
    rows: withProj[0].rows,
    columns: withProj[0].columns,
    proj0,
    idToK: new Map(withProj.map((p, sliceIndex) => [p.id, sliceIndex])),
  };
}

/** patient point -> continuous CT index { colIndex, rowIndex, sliceIndex } */
export function patientToCtIndex(geom, P) {
  const d = sub(P, geom.ipp0);
  return {
    colIndex: dot(d, geom.rowCos) / geom.colSp,
    rowIndex: dot(d, geom.colCos) / geom.rowSp,
    sliceIndex: (dot(P, geom.normal) - geom.proj0) / geom.dz,
  };
}

/** CT index (colIndex, rowIndex) -> patient point (in-plane; z chosen via slice 0 anchor) */
export function ctPixelToPatient(geom, colIndex, rowIndex) {
  return [
    geom.ipp0[0] + colIndex * geom.colSp * geom.rowCos[0] + rowIndex * geom.rowSp * geom.colCos[0],
    geom.ipp0[1] + colIndex * geom.colSp * geom.rowCos[1] + rowIndex * geom.rowSp * geom.colCos[1],
    geom.ipp0[2] + colIndex * geom.colSp * geom.rowCos[2] + rowIndex * geom.rowSp * geom.colCos[2],
  ];
}

/** crossing-number point-in-polygon; poly = [{x, y}, ...] */
function pointInPolygon(px, py, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const intersect =
      ((yi > py) !== (yj > py)) &&
      (px < ((xj - xi) * (py - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/** true if (px,py) lies on segment a–b (within eps, in pixel units) */
function onSegment(px, py, a, b, eps = 1e-6) {
  const cx = b.x - a.x, cy = b.y - a.y, L = cx * cx + cy * cy;
  let t = L ? ((px - a.x) * cx + (py - a.y) * cy) / L : 0;
  t = Math.max(0, Math.min(1, t));
  const dx = px - (a.x + t * cx), dy = py - (a.y + t * cy);
  return dx * dx + dy * dy <= eps * eps;
}

function onBoundary(px, py, poly) {
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    if (onSegment(px, py, poly[j], poly[i])) return true;
  }
  return false;
}



/**
 * Rasterize an ROI's per-slice polygons into per-CT-slice 2D binary masks,
 * cropped to the ROI bbox for memory.
 */
export function rasterizeRoiToCtMask(contoursByCtSlice, geom) {
  if (!contoursByCtSlice?.length) return null;

  let colMin = Infinity, colMax = -Infinity, rowMin = Infinity, rowMax = -Infinity;
  let sliceMin = Infinity, sliceMax = -Infinity;
  const bySlice = new Map(); // sliceIndex -> [polygon, ...]

  for (const { ctImageId, points } of contoursByCtSlice) {
    const sliceIndex = geom.idToK.get(ctImageId);
    if ( sliceIndex == null) continue; // slice metadata missing — skip
    for (const p of points) {
      if (p.x < colMin) colMin = p.x;
      if (p.x > colMax) colMax = p.x;
      if (p.y < rowMin) rowMin = p.y;
      if (p.y > rowMax) rowMax = p.y;
    }
    if ( sliceIndex < sliceMin) sliceMin = sliceIndex ;
    if ( sliceIndex > sliceMax) sliceMax = sliceIndex;
    if (!bySlice.has(sliceIndex)) bySlice.set(sliceIndex , []);
    bySlice.get(sliceIndex).push(points);
  }
  if (!bySlice.size || !isFinite(colMin)) return null;

  // integer bbox, pad 1, clamp to grid
  colMin = Math.max(0, Math.floor(colMin) - 1);
  rowMin = Math.max(0, Math.floor(rowMin) - 1);
  colMax = Math.min(geom.columns - 1, Math.ceil(colMax) + 1);
  rowMax = Math.min(geom.rows - 1, Math.ceil(rowMax) + 1);
  const bw = colMax - colMin + 1;
  const bh = rowMax - rowMin + 1;
  if (bw <= 0 || bh <= 0) return null;

  const masks = new Map();
  for (const [sliceIndex, polys] of bySlice) {
    const buf = new Uint8Array(bw * bh);
    for (let rowIndex = rowMin; rowIndex <= rowMax; rowIndex++) {
      for (let colIndex = colMin; colIndex <= colMax; colIndex ++) {
        let onEdge = false;
        let parity = false;
        for (const poly of polys) {
          if (onBoundary(colIndex, rowIndex, poly)) { onEdge = true; break; }
          if (pointInPolygon(colIndex, rowIndex, poly)) parity = !parity;
        }
        if (onEdge || parity) buf[(rowIndex - rowMin) * bw + (colIndex - colMin)] = 1;
      }
    }
    masks.set(sliceIndex, buf);
  }

  return { masks, bbox: { colMin, colMax, rowMin, rowMax, bw, bh }, sliceMin, sliceMax };
}

/**
 * Build fracAt(patientPoint) -> trilinear coverage in [0,1].
 * Bilinear within each of the two bracketing CT slices, then linear in k.
 */
export function makeFracSampler(rasterized, geom) {
  const { masks, bbox, sliceMin, sliceMax } = rasterized;
  const { colMin, colMax, rowMin, rowMax, bw } = bbox;

  const maskAt = (sliceIndex, rowIndex, colIndex) => {
    if (sliceIndex < sliceMin || sliceIndex > sliceMax) return 0;
    if (colIndex < colMin || colIndex > colMax || rowIndex < rowMin || rowIndex > rowMax) return 0;
    const slice = masks.get(sliceIndex);
    if (!slice) return 0;
    return slice[(rowIndex - rowMin) * bw + (colIndex - colMin)];
  };

  return function fracAt(P) {
    const { colIndex, rowIndex, sliceIndex } = patientToCtIndex(geom, P);
    const c0 = Math.floor(colIndex);
    const r0 = Math.floor(rowIndex);
    const k0 = Math.floor(sliceIndex);
    const tc = colIndex - c0;
    const tr = rowIndex - r0;
    const tk = sliceIndex - k0;

    const bilinear = kk => {
      const m00 = maskAt(kk, r0, c0);
      const m01 = maskAt(kk, r0, c0 + 1);
      const m10 = maskAt(kk, r0 + 1, c0);
      const m11 = maskAt(kk, r0 + 1, c0 + 1);
      const top = m00 * (1 - tc) + m01 * tc;
      const bot = m10 * (1 - tc) + m11 * tc;
      return top * (1 - tr) + bot * tr;
    };

    return bilinear(k0) * (1 - tk) + bilinear(k0 + 1) * tk;
  };
}
